import type { Player } from "@/lib/types";
import { cn } from "@/lib/cn";
import { usePlayerPhoto } from "./usePlayerPhoto";

function initialsFor(player: Player): string {
  return player.display_name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "--";
}

export function PlayerAvatar({
  player,
  size = "md",
  className,
}: {
  player: Player;
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  const url = usePlayerPhoto("player", player.id, player.photo_filename);
  const jersey = player.jersey_number?.trim();
  const sizeClass = {
    sm: "h-9 w-9 text-xs",
    md: "h-11 w-11 text-sm",
    lg: "h-14 w-14 text-base",
  }[size];
  const badgeClass = {
    sm: "min-w-4 px-0.5 text-[0.55rem]",
    md: "min-w-5 px-1 text-[0.6rem]",
    lg: "min-w-6 px-1 text-[0.68rem]",
  }[size];

  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded border border-rule bg-field font-display font-semibold text-ink",
        sizeClass,
        className,
      )}
      aria-hidden="true"
    >
      {url ? (
        <img
          src={url}
          alt=""
          className="h-full w-full object-cover"
          draggable={false}
        />
      ) : (
        initialsFor(player)
      )}
      {jersey ? (
        <span
          className={cn(
            "absolute bottom-0 right-0 inline-flex h-4 items-center justify-center rounded-tl border border-rule-strong bg-accent font-mono font-semibold leading-none text-accent-ink shadow-sm",
            badgeClass,
          )}
        >
          {jersey.slice(0, 3).toUpperCase()}
        </span>
      ) : null}
    </span>
  );
}
